import { PasswordResetForm } from "@/components/auth/PasswordResetForm";
import SignInForm from "@/components/auth/SignInForm";
import SignUpForm from "@/components/auth/SignUpForm";
import { useAuthForm } from "@/hooks/use-auth-form";
import useRedirectUrl from "@/hooks/useRedirectUrl";
import { supabase } from "@/integrations/supabase/client";
import type { ProfilePreferences } from "@/types/profile";
import { parseAsStringEnum, useQueryState } from "nuqs";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

enum AuthMode {
  SIGNIN = "sign-in",
  SIGNUP = "sign-up",
  FORGOT_PASSWORD = "forgot-password",
}

const Auth = () => {
  const [authMode, setAuthMode] = useQueryState<AuthMode>(
    "action",
    parseAsStringEnum<AuthMode>(Object.values(AuthMode))
      .withDefault(AuthMode.SIGNUP)
      .withOptions({ clearOnDefault: true })
  );

  const [redirectUrl] = useRedirectUrl();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { handleResetPassword, handleConfirmPassword } = useAuthForm();

  const handleAuthSuccess = useCallback(async () => {
    // if redirectUrl is set, navigate to it beyond other checks
    if (redirectUrl) {
      navigate(redirectUrl);
      return;
    }

    const {
      data: { session },
      error: getSessionError,
    } = await supabase.auth.getSession();

    if (getSessionError) {
      console.error("Error getting session:", getSessionError);
      throw new Error("Error getting session");
    }

    const { data: profile, error: getProfileError } = await supabase
      .from("profiles")
      .select("preferences")
      .eq("id", session.user.id)
      .single();

    if (getProfileError) {
      console.error("Error getting profile:", getProfileError);
      throw new Error("Error getting profile");
    }

    const { onboardingCompleted } = profile.preferences as ProfilePreferences;
    const destination = onboardingCompleted ? "/home" : "/onboarding";
    navigate(destination);

  }, [navigate, redirectUrl]);

  const [isRequestingNewLink, setIsRequestingNewLink] = useState(false);
  const handleRequestNewLink = async (email: string | null) => {
    if (email) {
      setIsRequestingNewLink(true);
      try {
        // Note: This flow doesn't have captcha yet since it's typically triggered from an authenticated context
        await handleResetPassword(email);
        setAuthMode(AuthMode.SIGNIN);
      } catch (error) {
        console.error("Error requesting new link:", error);
        setAuthMode(AuthMode.FORGOT_PASSWORD);
      } finally {
        setIsRequestingNewLink(false);
      }

    } else {
      // can't find email, back to the forgot password form so user can enter it
      setAuthMode(AuthMode.FORGOT_PASSWORD);
    }
  }

  const [isRequestingPasswordReset, setIsRequestingPasswordReset] = useState(false);
  const handlePasswordReset = async (e: React.FormEvent, captchaToken: string) => {
    e?.preventDefault();
    try {
      setIsRequestingPasswordReset(true);
      await handleResetPassword(email, captchaToken);
      setAuthMode(AuthMode.SIGNIN);
    } catch (error) {
      console.error("Error resetting password:", error);
    } finally {
      setIsRequestingPasswordReset(false);
    }
  };

  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const confirmNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsUpdatingPassword(true);
      await handleConfirmPassword(password);
    } catch (error) {
      console.error("Error updating password:", error);
      const message = error.message || "Failed to update password";
      alert(message);
      setAuthMode(AuthMode.FORGOT_PASSWORD);
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-primary text-primary-foreground flex flex-col items-center justify-center p-4 pb-0">
      <div className="w-full max-w-md space-y-8 mb-4">
        <div className="text-center space-y-6">
          <img
            src="/lovable-uploads/cee94e78-b677-411d-9ba5-f432a1142eac.png"
            alt="Nimi"
            className="h-20 mx-auto brightness-200 filter"
          />
          <h1 className="text-2xl md:text-4xl font-bold font-nexa leading-tight text-secondary">
            Experience the joy of art,
            <br />
            together
          </h1>
          <p className="text-muted-foreground text-lg">
            Art is better with friends, find yours here
          </p>
        </div>

        {authMode === AuthMode.FORGOT_PASSWORD && (
          <PasswordResetForm
            email={email}
            setEmail={setEmail}
            isSubmitting={isRequestingPasswordReset}
            onSubmit={handlePasswordReset}
            onBack={() => setAuthMode(AuthMode.SIGNIN)}
          />
        )}

        {authMode === AuthMode.SIGNIN && (
          <div className="space-y-4">
            <SignInForm
              onToggleMode={() => setAuthMode(AuthMode.SIGNUP)}
              onSuccess={handleAuthSuccess}
              onForgotPassword={() => setAuthMode(AuthMode.FORGOT_PASSWORD)}
            />
          </div>
        )}

        {authMode === AuthMode.SIGNUP && (
          <div className="space-y-4">
            <SignUpForm
              onSwitchToSignIn={() => setAuthMode(AuthMode.SIGNIN)}
              onSuccess={handleAuthSuccess}
              onForgotPassword={() => setAuthMode(AuthMode.FORGOT_PASSWORD)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Auth;
